import {models} from 'powerbi-client';

import {
  POWER_BI_PAGES,
  POWER_BI_REPORT_BOOKMARKS,
  POWER_BI_REPORT_FILTERS,
  POWER_BI_REPORTS,
  POWER_BI_SLICERS,
  DEFAULT_PROJECTS_SCHEMAS,
  VISUALS_TYPES_USER_CAN_SHARE_WITH_COPILOT,
  POWER_BI_VISUALS,
  POWER_BI_PROJECT_SLICER_NAME
} from '../const';
import {generateUniqueIdFromProjectName, getFrenchFormattedDate} from './data-formatting';

const createReportBasicFilter = (table, column, value) => {
  return {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: {
      table,
      column
    },
    operator: 'In',
    values: [value],
    filterType: models.FilterType.Basic
  };
};

// TODO see if this is still used somehow ?
const saveCurrentProjectToLocalStorage = data => {
  const projectName = data.dataPoints[0].identity[0].equals;
  localStorage.setItem('project', generateUniqueIdFromProjectName(projectName));
};

const areReportViewsFeaturesEnabled = async report => {
  if (!report) return;

  const pages = await report.getPages();
  const activePage = pages.find(p => p.isActive);
  const visuals = await activePage.getVisuals();
  const slicers = visuals.filter(v => v.type === 'slicer');
  const projectFilterSlicer = visuals.find(f => f.title === POWER_BI_PROJECT_SLICER_NAME);

  const isVisible = projectFilterSlicer?.layout.displayState.mode === 0;

  return !isVisible || (isVisible && slicers.length !== 1);
};

const setProjectFilterSlicer = async (report, projectName) => {
  const column = 'Nom du projet';
  const targetTable = 'Projets';
  const filter = createReportBasicFilter(targetTable, column, projectName);

  const page = await report.getActivePage();
  const visuals = await page.getVisuals();
  const projectFilterSlicer = visuals.find(f => f.title === POWER_BI_PROJECT_SLICER_NAME);
  await projectFilterSlicer?.setSlicerState({
    filters: [filter]
  });
};

const getCompanyReportTheme = async (isCustomReport, selectedReport) => {
  const {VITE_COMPANY_NAME, VITE_REPORT_COLOR_1, VITE_REPORT_COLOR_2, VITE_REPORT_COLOR_3, VITE_REPORT_COLOR_4, VITE_REPORT_COLOR_5, VITE_REPORT_COLOR_6, VITE_REPORT_COLOR_7, VITE_REPORT_COLOR_8} =
    import.meta.env;

  const th = await selectedReport?.getTheme();
  const defaultThemeColors = th?.themeJson?.dataColors;

  let dataColors = defaultThemeColors;

  if (!isCustomReport) {
    dataColors = [VITE_REPORT_COLOR_1, VITE_REPORT_COLOR_2, VITE_REPORT_COLOR_3, VITE_REPORT_COLOR_4, VITE_REPORT_COLOR_5, VITE_REPORT_COLOR_6, VITE_REPORT_COLOR_7, VITE_REPORT_COLOR_8];
  }

  const themeJson = {
    name: `${VITE_COMPANY_NAME} theme`,
    dataColors,
    visualStyles: {
      page: {
        '*': {
          outspace: [
            {
              color: {
                solid: {
                  color: import.meta.env.VITE_BACKGROUND_COLOR
                }
              }
            }
          ]
        }
      }
    }
  };

  return themeJson;
};

const getReportIframeHeight = (windowHeight, bannerHeight) => {
  const headerHeight = 64;

  return windowHeight - headerHeight - bannerHeight;
};

const getReportsIdsHavingUploadButton = reports => {
  return reports.filter(report => report?.schema_name.includes(DEFAULT_PROJECTS_SCHEMAS.gestion)).map(r => r.report_id);
};

const getAllVisualsOfReport = async report => {
  const pages = await report.getPages();
  const promises = pages.map(p => p.getVisuals());
  const allVisuals = await Promise.all(promises);
  return allVisuals.flat();
};

const getVisualsUserCanSendThroughChatbot = async (report, isCustomReport) => {
  const visuals = await getAllVisualsOfReport(report);
  const allowedVisualsByTypes = visuals.filter(v => VISUALS_TYPES_USER_CAN_SHARE_WITH_COPILOT.includes(v.type));

  // When inside a custom report (not a DeFi one), user can choose any visuals.
  if (isCustomReport) {
    return allowedVisualsByTypes;
  }
  // We filter mobile visuals, user cannot select them (for now, only done by title)
  const desktopVisuals = allowedVisualsByTypes.filter(v => !v.title.toLowerCase().includes('mobile'));
  // We filter visuals that don't have a title
  const visualsWithTitle = desktopVisuals.filter(v => v.title !== '');
  // Business rule : for now we filter visuals named "Matrice"
  const visualsWithoutMatrixes = visualsWithTitle.filter(v => v.title !== 'Matrice');

  return visualsWithoutMatrixes;
};

const shouldUpdateToken = async tokenExpiration => {
  const MINUTES_BEFORE_EXPIRATION = 2;
  const currentTime = Date.now();
  const expiration = typeof tokenExpiration === 'number' ? tokenExpiration : Date.parse(tokenExpiration);

  const timeUntilExpiration = expiration - currentTime;
  const timeToUpdate = MINUTES_BEFORE_EXPIRATION * 60 * 1000; // We consider refreshing token 2 minutes before its expiration

  return timeUntilExpiration <= timeToUpdate;
};

function powerBiCsvToJson(csvData) {
  const lines = csvData.trim().split('\n');
  const headers = lines[0].split(',');

  const jsonArray = lines.slice(1).map(line => {
    const values = line.split(',');
    const obj = {};
    headers.forEach((header, index) => {
      let value = values[index].trim();
      if (header.trim() === 'Montant') {
        value = parseFloat(value);
      }
      obj[header.trim()] = value;
    });
    return obj;
  });

  return jsonArray;
}

const getAccountantExpectedElementsVisualJsonData = async page => {
  try {
    const journalVisual = await page.getVisualByName(POWER_BI_VISUALS.accountantExpectedElements.name);
    if (journalVisual) {
      const {data} = await journalVisual.exportData(models.ExportDataType.Summarized);
      const jsonFormattedData = powerBiCsvToJson(data);
      return jsonFormattedData;
    }
    return [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error({error});
    return [];
  }
};

const cleanSummaryVisualData = rawData => {
  if (!rawData) return ''; // Handle empty data safely

  // Remove "Résumé visuel" at the start (if present)
  let cleanedData = rawData.replace(/^Résumé visuel\s*/, '');

  // Remove surrounding quotes (handles variations including spaces)
  cleanedData = cleanedData.trim().replace(/^["']|["']$/g, '');

  return cleanedData;
};

export {
  createReportBasicFilter,
  saveCurrentProjectToLocalStorage,
  getCompanyReportTheme,
  setProjectFilterSlicer,
  getReportIframeHeight,
  getReportsIdsHavingUploadButton,
  getVisualsUserCanSendThroughChatbot,
  shouldUpdateToken,
  getAccountantExpectedElementsVisualJsonData,
  getAllVisualsOfReport,
  cleanSummaryVisualData,
  areReportViewsFeaturesEnabled
};
